import { DEFAULT_LOCALE, formatDate, intlLocale, type AnyLocaleType } from './i18n/core.js';

export type WorkItemStateGroupType =
  | 'backlog'
  | 'unstarted'
  | 'started'
  | 'completed'
  | 'cancelled';

/** The priorities an item can have, most urgent first. */
export const WORK_ITEM_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;

export type WorkItemPriorityType = (typeof WORK_ITEM_PRIORITIES)[number];

export interface WorkItemStateLike {
  key?: string | null;
  statusKey?: string | null;
  name: string;
  group: WorkItemStateGroupType;
  color?: string | null;
}

export interface ProjectStatusLike {
  key: string;
  name: string;
  group: WorkItemStateGroupType;
  color?: string | null;
  position: number;
}

export interface WorkItemWithStateLike {
  state?: WorkItemStateLike | null;
}

export type CanonicalWorkItemStatusKey =
  | 'in_progress'
  | 'blocked'
  | 'resolved'
  | 'open'
  | 'backlog'
  | 'done'
  | 'cancelled'
  | 'no_state';

export interface CanonicalWorkItemStatus {
  key: string;
  label: string;
  group: WorkItemStateGroupType | 'no-state';
  color?: string | null;
  position: number;
}

/**
 * Relevance order, not lifecycle order: moving work first, finished work last.
 */
export const STATE_GROUP_ORDER: WorkItemStateGroupType[] = [
  'started',
  'unstarted',
  'backlog',
  'completed',
  'cancelled',
];

export const STATE_GROUP_LABELS: Record<WorkItemStateGroupType, string> = {
  backlog: 'Backlog',
  unstarted: 'Not started',
  started: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const CANONICAL_WORK_ITEM_STATUSES: Record<
  CanonicalWorkItemStatusKey,
  CanonicalWorkItemStatus
> = {
  in_progress: {
    key: 'in_progress',
    label: 'In progress',
    group: 'started',
    color: '#3b82f6',
    position: 0,
  },
  blocked: {
    key: 'blocked',
    label: 'Blocked',
    group: 'started',
    color: '#f97316',
    position: 10,
  },
  resolved: {
    key: 'resolved',
    label: 'Resolved',
    group: 'started',
    color: '#a855f7',
    position: 20,
  },
  open: {
    key: 'open',
    label: 'Open',
    group: 'unstarted',
    color: '#94a3b8',
    position: 30,
  },
  backlog: {
    key: 'backlog',
    label: 'Backlog',
    group: 'backlog',
    color: '#94a3b8',
    position: 40,
  },
  done: {
    key: 'done',
    label: 'Done',
    group: 'completed',
    color: '#22c55e',
    position: 50,
  },
  cancelled: {
    key: 'cancelled',
    label: 'Cancelled',
    group: 'cancelled',
    color: '#6b7280',
    position: 60,
  },
  no_state: {
    key: 'no_state',
    label: 'No state',
    group: 'no-state',
    color: '#e5e7eb',
    position: 70,
  },
};

export function canonicalStatusFor(item: WorkItemWithStateLike): CanonicalWorkItemStatus {
  return fallbackCanonicalStatusFor(item);
}

/** Where an item with no workflow state at all is grouped, on the server and here. */
export const WORK_ITEM_NO_STATUS_KEY = 'no_state';

/**
 * The key the server groups an item under: `COALESCE(status_key, key, 'no_state')`.
 *
 * ⛔ Not the section, and not `configuredStatusFor`. This is the *server's* grain — the value the
 * count query's `workflowStatus` axis answers with and the value a group-scoped page filters on.
 * `configuredStatusFor` folds several of these into one section, which is a coarser question.
 *
 * It is spelled here rather than at each caller because a group's heading count, the request that
 * pages that group, and the rule that decides whether a newly created item belongs in that group
 * are three readings that have to agree. When they did not, a group's count and the items under it
 * were answers to different questions (I-0308).
 */
export function workItemServerStatusKey(item: WorkItemWithStateLike): string {
  const state = item.state;
  return state?.statusKey ?? state?.key ?? WORK_ITEM_NO_STATUS_KEY;
}

export function configuredStatusFor(
  item: WorkItemWithStateLike,
  projectStatuses: ProjectStatusLike[],
): CanonicalWorkItemStatus {
  const state = item.state;
  const configured = projectStatuses.find((status) => {
    if (state?.statusKey && status.key === state.statusKey) return true;
    return state?.key ? status.key === state.key : false;
  });
  if (configured) {
    return {
      key: configured.key,
      label: configured.name,
      group: configured.group,
      color: configured.color,
      position: configured.position,
    };
  }
  return fallbackCanonicalStatusFor(item);
}

function fallbackCanonicalStatusFor(item: WorkItemWithStateLike): CanonicalWorkItemStatus {
  const state = item.state;
  if (!state) return CANONICAL_WORK_ITEM_STATUSES.no_state;

  const rawKey = (state.key ?? state.name).toLowerCase().replace(/[\s_]+/g, '-');
  if (['in-progress', 'partial', 'doing', 'started', 'active'].includes(rawKey)) {
    return CANONICAL_WORK_ITEM_STATUSES.in_progress;
  }
  if (rawKey === 'blocked') return CANONICAL_WORK_ITEM_STATUSES.blocked;
  if (['resolved', 'fixed', 'addressed', 'implemented'].includes(rawKey)) {
    return CANONICAL_WORK_ITEM_STATUSES.resolved;
  }
  if (['done', 'shipped', 'closed', 'complete', 'completed'].includes(rawKey)) {
    return CANONICAL_WORK_ITEM_STATUSES.done;
  }
  if (
    [
      'cancelled',
      'canceled',
      'wont-fix',
      "won't-fix",
      'duplicate',
      'abandoned',
      'superseded',
      'deprecated',
      'not-relevant',
    ].includes(rawKey)
  ) {
    return CANONICAL_WORK_ITEM_STATUSES.cancelled;
  }
  if (['proposed', 'backlog'].includes(rawKey)) return CANONICAL_WORK_ITEM_STATUSES.backlog;
  if (['new', 'open', 'todo', 'triage', 'triaged', 'designed', 'deferred'].includes(rawKey)) {
    return CANONICAL_WORK_ITEM_STATUSES.open;
  }

  if (state.group === 'completed') return CANONICAL_WORK_ITEM_STATUSES.done;
  if (state.group === 'cancelled') return CANONICAL_WORK_ITEM_STATUSES.cancelled;
  if (state.group === 'started') return CANONICAL_WORK_ITEM_STATUSES.in_progress;
  if (state.group === 'backlog') return CANONICAL_WORK_ITEM_STATUSES.backlog;
  return CANONICAL_WORK_ITEM_STATUSES.open;
}

export const TERMINAL_STATE_GROUPS: WorkItemStateGroupType[] = ['completed', 'cancelled'];

export function isTerminalGroup(group?: string | null): boolean {
  return TERMINAL_STATE_GROUPS.includes(group as WorkItemStateGroupType);
}

const MCP_WORK_AREA_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Why a work-area id argument cannot be used, or `null` when it can (I-0692).
 *
 * Both MCP servers, stdio (`code/mcp/src/server.ts`) and Streamable HTTP (`code/backend/src/mcp/`),
 * ask this before a `workAreaId` or `projectId` reaches a query. A value that is not a UUID used to
 * reach Postgres, and the caller got back its own `invalid input syntax for type uuid`: database text
 * on the wire, and nothing an assistant could act on. One sentence, here, so the two servers cannot
 * say it differently.
 */
export function mcpWorkAreaIdProblem(
  name: 'workAreaId' | 'projectId',
  value: string,
  /** `reference` where the server takes a `workArea` reference (HTTP); `ids` where it does not. */
  server: 'reference' | 'ids',
): string | null {
  if (MCP_WORK_AREA_UUID.test(value)) return null;
  const shown = value.length > 80 ? `${value.slice(0, 80)}…` : value;
  const remedy =
    server === 'reference'
      ? 'To name a work area by reference, pass workArea instead: namespace/slug, or the ' +
        'workAreaKey that fetch returns.'
      : '`featit projects --json` lists each work area with its id.';
  return `${name} takes a work-area UUID, and ${JSON.stringify(shown)} is not one. ${remedy}`;
}

/**
 * The workflow state key `featit_item_change` close moves an item to. One definition, read by both MCP
 * surfaces — `code/mcp/src/server.ts` (stdio) and `code/backend/src/mcp/` (Streamable HTTP,
 * I-0613 AC-1.2) — so the two cannot independently drift on what "close" means. A third, HTTP-only
 * definition of the same mapping would be exactly the restated rule `CLAUDE.md` warns disagrees with
 * itself the first time only one copy is edited.
 *
 * ⚠️ **No longer per item-type.** `CollapseWorkflowStatesToStatusCategories` retired the per-type
 * state names (`shipped`, `closed`, `proposed`, `new`, …) this function used to switch on — every
 * item type now resolves to the same seven status-category keys (`CANONICAL_WORK_ITEM_STATUSES`),
 * of which `done` is the one `completed`-group key. The `itemTypeKey` parameter is kept so neither
 * MCP server has to change its call site if a type ever needs its own terminal state again; today
 * every type answers the same.
 */
export function mcpTerminalWorkflowStateKeyFor(_itemTypeKey: string): string {
  return CANONICAL_WORK_ITEM_STATUSES.done.key;
}

/** The workflow state key `featit_item_change` reopen moves an item to. See the sibling above — `open` is the
 * `unstarted`-group key every item type starts in. */
export function mcpReopenWorkflowStateKeyFor(_itemTypeKey: string): string {
  return CANONICAL_WORK_ITEM_STATUSES.open.key;
}

/** Urgent first, matching the order the dispatch queue hands work out in. */
export const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const NO_PRIORITY_RANK = 90;
const UNKNOWN_PRIORITY_RANK = 80;

/** Urgent first, then unknown priorities, then no priority at all (I-0900: reused by "Tasks for me"). */
export function priorityRank(priority?: string | null): number {
  if (!priority) return NO_PRIORITY_RANK;
  return PRIORITY_RANK[priority] ?? UNKNOWN_PRIORITY_RANK;
}

function targetDateRank(value?: string | null): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

export type SortableWorkItem = {
  priority?: string | null;
  targetDate?: string | null;
  position?: number;
  createdAt?: string | null;
};

export function sortByPriority<T extends SortableWorkItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
    if (byPriority !== 0) return byPriority;

    const dateA = targetDateRank(a.targetDate);
    const dateB = targetDateRank(b.targetDate);
    if (dateA !== dateB) return dateA < dateB ? -1 : 1;

    const byPosition = (a.position ?? 0) - (b.position ?? 0);
    if (byPosition !== 0) return byPosition;

    return createdAtRank(a.createdAt) - createdAtRank(b.createdAt);
  });
}

function createdAtRank(value?: string | null): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function parseUtcTimestamp(value: string): Date {
  const withTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
  const millisPrecision = withTimezone.replace(/\.(\d{3})\d+/, '.$1');
  return new Date(millisPrecision);
}

/*
 * I-0902: one formatter per shape, built on first use. `toLocaleString` with options builds a new
 * `Intl.DateTimeFormat` on every call, and a list row calls these on every render: scrolling 3,000
 * items spent a sixth of its script time here. Same locale (the runtime's), same output.
 */
const DATE_FORMATS = new Map<string, Intl.DateTimeFormat>();

function dateFormat(options: Intl.DateTimeFormatOptions, locale?: string): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  let format = DATE_FORMATS.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    DATE_FORMATS.set(key, format);
  }
  return format;
}

export function formatTargetDate(value: string, now = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const sameYear = date.getFullYear() === now.getFullYear();
  return dateFormat({
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/**
 * I-1124: a timestamp is formatted once, not once per render of every row that shows it. A list
 * draws a row's title with it again on every move of its window, and parsing and formatting each
 * again was a measurable share of the move at 4×. Bounded, so a long session does not grow it.
 */
const FORMATTED_TIMESTAMPS = new Map<string, string>();
const FORMATTED_TIMESTAMPS_LIMIT = 5000;
const TIMESTAMP: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * T-0473 IN-6: a moment in the locale it is given (the page's, from `useLocale()`); the CLI and the
 * server keep English. Kept by the tag it was formatted in, so a language or region switch reads anew.
 */
export function formatTimestamp(value: string, locale: AnyLocaleType = DEFAULT_LOCALE): string {
  const key = `${intlLocale(locale)} ${value}`;
  const known = FORMATTED_TIMESTAMPS.get(key);
  if (known !== undefined) return known;
  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = formatDate(locale, date, TIMESTAMP);
  if (FORMATTED_TIMESTAMPS.size >= FORMATTED_TIMESTAMPS_LIMIT) FORMATTED_TIMESTAMPS.clear();
  FORMATTED_TIMESTAMPS.set(key, formatted);
  return formatted;
}

export function isOverdue(value?: string | null, now = new Date()): boolean {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return date < today;
}

export function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  return value.slice(0, 10);
}

export function stripMarkdown(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[(.+?)\]\(.*?\)/g, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^>\s+/gm, '')
    .replace(/^---+$/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
